import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get, isArray } from 'lodash';
import { GridRowId, GridRowModel, GridRowParams } from '@mui/x-data-grid-pro';
import { deleteCertificate, getAllCertificates, updateCertificate } from './_api';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useWithTitle } from 'utils/hooks/useWithTitle';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useEntityRemove } from 'utils/hooks/useEntityRemove';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';
import { useAppDispatch } from 'store/hooks';
import { storeConfirmationDataToStore } from 'store/reducers/appReducer';
import { ICertificate } from './_types';
import CertificatesComponent from './CertificatesComponent';
import EditFileDialog from './EditFileDialog';
import useAlerts from 'components/Alerts/useAlerts';

const muiGridKey = 'certificatesMUI';

export const Certificates: React.FC = () => {
  const { t } = useTranslation('Certificates');
  useWithTitle();
  const { toggleLoader } = useAppGlobals();
  const dispatch = useAppDispatch();
  const { confirmationData } = useAppInfo();
  const { addErrorAlert, addSuccessAlert } = useAlerts();

  const [certificates, setCertificates] = useState<any[]>([]);
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<GridRowId[]>([]);
  const [editDialogState, setEditDialogState] = useState<boolean>(false);
  const [fileToEdit, setFileToEdit] = useState<any>({});

  const getRowId = useCallback((row: GridRowModel) => row.username, []);

  const clearAction = () => {
    dispatch(storeConfirmationDataToStore(null));
  };

  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  const columns = reorderColumnsByGridSettings(
    injectColumnWidthsIntoColumns([
      { field: 'lastName', headerName: t('Users:lastname') },
      { field: 'firstName', headerName: t('Users:firstname') },
    ]),
  );

  const loadEntity = async () => {
    toggleLoader();
    try {
      const certificates = await getAllCertificates();
      if (certificates && isArray(certificates)) {
        setCertificates(certificates);
      } else {
        setCertificates([]);
      }
    } catch (e) {
      console.debug(e);
    }
    toggleLoader(false);
  };

  useEffect(() => {
    loadEntity();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { onEntityRemove } = useEntityRemove(deleteCertificate, t, loadEntity);

  const confirmAction = async () => {
    await onEntityRemove(get(confirmationData, 'id'));
    clearAction();
  };

  const handleDetailPanelExpandedRowIdsChange = React.useCallback((newIds: GridRowId[]) => {
    setDetailPanelExpandedRowIds(newIds);
  }, []);

  const DetailPanel = ({ row }: GridRowParams) => {
    const userCertificates: ICertificate[] = get(row, 'certificates', []);
    const username: string = get(row, 'username', '');
    return (
      <CertificatesComponent
        userCertificates={userCertificates}
        username={username}
        setFileToEdit={setFileToEdit}
        toggleEditDialog={toggleEditDialog}
      />
    );
  };

  const updateFilename = async (values: any) => {
    toggleLoader();
    const resp = await updateCertificate({
      name: values.name,
      id: fileToEdit.id,
      favourite: fileToEdit.favourite,
    });
    if (resp) {
      toggleEditDialog(false);
      const expandedIds = detailPanelExpandedRowIds;
      setDetailPanelExpandedRowIds([]);
      await loadEntity();
      setDetailPanelExpandedRowIds(expandedIds);
      addSuccessAlert(t('successEdit'));
    } else {
      addErrorAlert(t('errorEdit'));
    }
    toggleLoader(false);
  };

  const toggleEditDialog = (state: boolean = true) => {
    setEditDialogState(state);
  };

  return (
    <>
      <MuiGrid
        gridKey={muiGridKey}
        getRowId={getRowId}
        rows={certificates}
        columns={columns}
        initialSortMode={[{ field: 'lastName', sort: 'asc' }]}
        rowDetail={{
          showRowDetail: true,
          DetailPanel,
          getDetailPanelHeight: () => 'auto',
          detailPanelExpandedRowIds,
          handleDetailPanelExpandedRowIdsChange,
        }}
      />
      {confirmationData && confirmationData.id && !confirmationData.editDialog && (
        <ConfirmationDialog
          title={confirmationData.title ? t(confirmationData.title) : t('Grid:confirmDelete')}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={clearAction}
          confirmAction={confirmAction}
        />
      )}
      <EditFileDialog
        editDialogState={editDialogState}
        toggleEditDialog={toggleEditDialog}
        fileForEditDialog={fileToEdit}
        updateFiles={updateFilename}
      />
    </>
  );
};
